/**
 * Main/backup for the views (PLAN §8, D9; wave 7b-tail C): the overview (every
 * production with its active and standby node, graded), the standby's view,
 * and the operations — a production's `manualOnly`, a switchover, the whole
 * node's switchover and the emergency activation. Query/mutation option
 * factories over the API client (the data-hook pattern, `rundowns.ts`).
 *
 * **Why polled, not live.** The overview is not a resource the change channel
 * names (D35): it is a view of the pair's streams, which change many times a
 * second on the node and every 30 s in the cloud (the nodes' reports). So it
 * is re-read every `OVERVIEW_POLL_MS` while shown, and after every operation
 * here; the standby's view every `STANDBY_POLL_MS` (an operator compares it
 * with the live active beside it).
 */
import type { ApiClient } from '@acp/api-client/client.js';
import type { ForcePromoteResultWire, RedundancyOverviewWire, StandbyPlayoutWire } from '@acp/api-contract/pairStatus.js';
import type { ProductionSettingsWire, SwitchoverAllResponseWire, SwitchoverResultWire } from '@acp/api-contract/redundancy.js';
import { type QueryClient, queryOptions } from '@tanstack/react-query';

/** The overview's re-read while shown: the node's streams move fast; the cloud's reports come every 30 s. */
export const OVERVIEW_POLL_MS = 5_000;

/** The standby's view's re-read: fast enough to watch it follow a take. */
export const STANDBY_POLL_MS = 2_000;

export const redundancyKeys = {
  all: ['redundancy'] as const,
  overview: (siteId: string | null) => [...redundancyKeys.all, 'overview', siteId ?? '*'] as const,
  standby: (productionId: string) => [...redundancyKeys.all, 'standby', productionId] as const,
};

export function redundancyOverviewQuery(api: ApiClient, siteId: string | null) {
  return queryOptions({
    queryKey: redundancyKeys.overview(siteId),
    queryFn: async (): Promise<RedundancyOverviewWire> => api.call('getRedundancyOverview', { params: null, query: siteId === null ? {} : { siteId }, body: null }),
    refetchInterval: OVERVIEW_POLL_MS,
  });
}

export function standbyPlayoutQuery(api: ApiClient, productionId: string) {
  return queryOptions({
    queryKey: redundancyKeys.standby(productionId),
    queryFn: async (): Promise<StandbyPlayoutWire> => api.call('getStandbyPlayout', { params: { productionId }, query: null, body: null }),
    refetchInterval: STANDBY_POLL_MS,
  });
}

/** After any role change or setting: every overview (and standby view) is stale. */
async function refreshAll(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: redundancyKeys.all });
}

/** D9's per-production "manual only" (node only). */
export function updateRedundancyMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly productionId: string; readonly manualOnly: boolean }): Promise<ProductionSettingsWire> =>
      (await api.call('updateRedundancy', { params: { productionId: input.productionId }, query: null, body: { manualOnly: input.manualOnly } })).settings,
    onSuccess: () => refreshAll(queryClient),
  };
}

/** A production switched over to the other node (either host: the cloud relays it to the production's node). */
export function pairSwitchoverMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly productionId: string }): Promise<SwitchoverResultWire> => api.call('switchoverProduction', { params: { productionId: input.productionId }, query: null, body: {} }),
    onSuccess: () => refreshAll(queryClient),
  };
}

/** D9's "switch whole node": every production active on the node answering (node only). */
export function switchoverAllMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (): Promise<SwitchoverAllResponseWire> => api.call('switchoverAll', { params: null, query: null, body: {} }),
    onSuccess: () => refreshAll(queryClient),
  };
}

/** The emergency activation: force the answering node's standby on air (node only; docs/acp/redundancy.md). */
export function forcePromoteMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly productionId: string; readonly confirm: string; readonly reason: string }): Promise<ForcePromoteResultWire> =>
      api.call('forcePromoteProduction', { params: { productionId: input.productionId }, query: null, body: { confirm: input.confirm, reason: input.reason } }),
    onSuccess: () => refreshAll(queryClient),
  };
}
